/* My library — handoff screen 07-My-Library.png. Data: brightivity.dev/library (M4, shared with studioCloud). */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TopBar } from '../app/AppShell.jsx';
import {
  deleteDesign, fetchDesignImage, getCategories, librarySummary, listDesigns, makeThumbnail, putCategories, saveDesign, updateDesign,
} from '../api/library.js';
import { handToPrint } from '../features/print/handoff.js';
import { api } from '../api/client.js';
import LibraryCanva from '../features/browser/LibraryCanva.jsx';
import { Button, Card, EmptyState, Icon, Input, Modal, SearchInput, Segmented, Select, Skeleton, Tag } from '../ui/index.jsx';
import { usePrintStore } from '../features/print/PrintStore.jsx';
import { useSession } from '../app/session.jsx';
import AlignmentChip from '../components/shared/AlignmentChip.jsx';
import '../features/library/library.css';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n/index.js';

const PAGE = 8;
const KIT = ['purple', 'coral', 'green', 'blue', 'yellow'];
const KIT_HEX = { purple: '#7F4BF6', coral: '#FF5151', green: '#26C774', blue: '#1AB0F2', yellow: '#FFBD21' };
const SORTS = [{ value: 'newest', label: 'library.sort.newest' }, { value: 'oldest', label: 'library.sort.oldest' }, { value: 'name', label: 'library.sort.name' }, { value: 'source', label: 'library.sort.source' }];
// StudioCore PrintLibrary source badges.
const SOURCE = { printables: ['library.src.printables', 'yellow'], ai_generator: ['library.src.ai', 'purple'], canva: ['library.src.canva', 'blue'], editor: ['library.src.editor', 'green'], upload: ['library.src.upload', 'coral'] };
const UPLOAD_MAX_BYTES = 200 * 1024 * 1024; // desktop PrintLibrary limit
const isHex = (c) => /^#[0-9a-f]{6}$/i.test(String(c || ''));
/** The print hand-off for a library design (the original is copied server-side by the Print page). */
const printItem = (d) => ({ remote: true, posterId: (d.metadata && (d.metadata.posterId || d.metadata.printableId)) || null, name: d.name, previewUrl: d.previewUrl || d.thumbnailUrl || d.imageUrl, sourceUrl: d.imageUrl || d.thumbnailUrl });
/** Favourite on a catalog / AI poster also feeds poster_feedback (desktop IIG Phase 5), fire-and-forget. */
const feedbackFavorite = (d, on) => {
  const id = d.metadata && (d.metadata.posterId || d.metadata.printableId);
  if (id) api(`/printables/posters/${id}/feedback`, { method: 'POST', body: { favorite: on, source: 'library_card' } }).catch(() => {});
};
const day = (iso) => (iso ? new Date(iso).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' }) : '');

/** A folder's kit colour: its own if it is one of ours, else a stable pick by position. */
function folderColor(cat, i) {
  const c = String(cat.cat_color || '').toLowerCase();
  const hit = Object.entries(KIT_HEX).find(([, hex]) => hex.toLowerCase() === c) || (KIT.includes(c) ? [c] : null);
  return hit ? hit[0] : isHex(c) ? c : KIT[i % KIT.length];
}
/** A folder dot / tag: kit colours by class, a free colour by style. */
const dotProps = (color) => (isHex(color) ? { className: 'bv-folder__dot', style: { background: color } } : { className: `bv-folder__dot bv-dot--${color}` });

function DesignCard({ d, folder, selecting, selected, onOpen, onToggle, onFavorite }) {
  const { t } = useTranslation('bv');
  const fav = !!(d.metadata && d.metadata.favorite);
  const src = SOURCE[d.source];
  const pc = d.metadata && Number(d.metadata.pageCount);
  return (
    <div className={`bv-dcard${selected ? ' is-selected' : ''}`} data-testid="design-card" draggable
      onDragStart={(e) => { e.dataTransfer.setData('application/x-bv-design', d.id); e.dataTransfer.effectAllowed = 'move'; }}>
      <button type="button" className="bv-dcard__hit" onClick={selecting ? onToggle : onOpen} aria-pressed={selecting ? selected : undefined}
        aria-label={selecting ? t('library.card.select', { name: d.name }) : t('library.card.open', { name: d.name })} />
      <div className="bv-dcard__pic">
        {(d.previewUrl || d.thumbnailUrl) ? <img src={d.previewUrl || d.thumbnailUrl} alt="" loading="lazy" draggable={false} /> : <Icon name="image" size={40} />}
        {src && <Tag color={src[1]} className="bv-dcard__src" data-testid="source-badge">{t(src[0])}</Tag>}
        {pc > 1 && <span className="bv-dcard__pages" data-testid="page-badge">{t('library.pageOf', { n: (Number(d.metadata.pageIndex) || 0) + 1, total: pc })}</span>}
        {selecting ? (
          <span className={`bv-dcard__check${selected ? ' is-on' : ''}`} aria-hidden="true">{selected && <Icon name="check" size={18} />}</span>
        ) : (
          <button type="button" className={`bv-dcard__heart${fav ? ' is-on' : ''}`} aria-pressed={fav} aria-label={fav ? t('common.unfavourite') : t('library.card.favourite')} onClick={onFavorite}>
            <Icon name="heart" size={20} />
          </button>
        )}
      </div>
      <div className="bv-dcard__name">{d.name}</div>
      <div className="bv-dcard__meta">
        <span>{day(d.createdAt || d.created_at)}</span>
        {folder && isHex(folder.color)
          ? <span className="bv-tag" style={{ background: `${folder.color}22`, color: folder.color }}>{folder.cat_name}</span>
          : <span className={`bv-tag bv-tag--${folder ? folder.color : 'blue'}`}>{folder ? folder.cat_name : t('library.defaultFolder')}</span>}
      </div>
    </div>
  );
}

function DesignDetail({ d, folders, onClose, onChanged, onDeleted }) {
  const { t } = useTranslation('bv');
  const navigate = useNavigate();
  const { session } = useSession();
  const [full, setFull] = useState(false);
  const [name, setName] = useState(d.name);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const save = async (patch) => {
    setBusy('save'); setErr(null);
    const r = await updateDesign(d.id, patch);
    setBusy(null);
    if (!r.ok) setErr(r.error); else onChanged({ ...d, ...r });
  };
  const print = async () => {
    setBusy('print'); setErr(null);
    const sourceUrl = d.imageUrl || d.thumbnailUrl;
    if (!sourceUrl) { setErr(t('library.detail.noPicturePrint')); setBusy(null); return; }
    // The original (often 100 MB) is copied server-side by the Print page; only the thumbnail loads here.
    handToPrint([printItem(d)]);
    navigate('/print');
  };
  /** Open in Design: an editor design reopens with its layers (saves back in place); any other picture
   * starts a new design with the picture placed (StudioCore PrintLibrary "Edit"). */
  const edit = async () => {
    setBusy('edit'); setErr(null);
    try {
      if (d.designJsonUrl) {
        const r = await api(`/library/designs/${d.id}/json`); // the layers are private: signed-in route
        if (!r.ok || !r.data) throw new Error(r.error || t('library.detail.openFailed'));
        window.pendingDesignLoad = { designId: d.id, json: r.data, name: d.name };
      } else {
        const imageUrl = d.imageUrl || d.previewUrl || d.thumbnailUrl;
        if (!imageUrl) throw new Error(t('library.detail.noPictureEdit'));
        window.pendingDesignLoad = { designId: null, json: null, name: d.name, imageUrl };
      }
      navigate('/design');
    } catch (e) { setErr(String(e.message || e)); setBusy(null); }
  };
  const download = async () => {
    setBusy('download');
    try {
      const f = await fetchDesignImage(d);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(f); a.download = f.name; a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 4000);
    } catch (e) { setErr(String(e.message || e)); }
    setBusy(null);
  };
  const remove = async () => {
    setBusy('delete');
    const r = await deleteDesign(d.id);
    setBusy(null);
    if (!r.ok) setErr(r.error); else onDeleted(d.id);
  };
  return (
    <Modal open title={d.name} onClose={onClose} width={860}>
      <div className="bv-ddetail" data-testid="design-detail">
        <div className={`bv-ddetail__pic${full ? ' is-full' : ''}`} data-testid="detail-preview">
          {(d.previewUrl || d.thumbnailUrl) ? <img src={full ? (d.imageUrl || d.previewUrl || d.thumbnailUrl) : (d.previewUrl || d.thumbnailUrl)} alt={d.name} /> : <Icon name="image" size={60} />}
          <div className="bv-ddetail__zoom">
            <Segmented value={full ? 'full' : 'fit'} onChange={(v) => setFull(v === 'full')} options={[{ value: 'fit', label: t('library.detail.fit') }, { value: 'full', label: t('library.detail.full') }]} />
          </div>
        </div>
        <div className="bv-ddetail__side">
          <label className="bv-label" htmlFor="bv-dname">{t('common.name')}</label>
          <div className="bv-ddetail__row">
            <Input id="bv-dname" value={name} maxLength={300} onChange={(e) => setName(e.target.value)} />
            <Button size="sm" disabled={!name.trim() || name === d.name || !!busy} onClick={() => save({ name: name.trim() })}>{t('common.save')}</Button>
          </div>
          <label className="bv-label" htmlFor="bv-dfolder">{t('library.folder')}</label>
          <Select id="bv-dfolder" value={d.category || ''} onChange={(e) => save({ category: e.target.value || null })}
            options={[{ value: '', label: t('library.defaultFolder') }, ...folders.map((f) => ({ value: f.cat_uuid, label: f.cat_name }))]} />
          <p className="bv-small bv-muted" data-testid="detail-meta">{d.metadata && Number(d.metadata.pageCount) > 1 ? `${t('library.pageOf', { n: (Number(d.metadata.pageIndex) || 0) + 1, total: d.metadata.pageCount })} · ` : ''}{t(`library.from.${['printables', 'ai_generator', 'canva', 'editor'].includes(d.source) ? d.source : 'upload'}`)} · {day(d.createdAt || d.created_at)}{d.width && d.height ? ` · ${d.width} × ${d.height} px` : ''}</p>
          {d.metadata && d.metadata.alignment && <AlignmentChip alignment={d.metadata.alignment} viewerStateCode={session && session.schoolStateCode} />}
          <div className="bv-ddetail__actions">
            <Button variant="coral" icon="print" block disabled={!!busy} onClick={print}>{busy === 'print' ? t('common.opening') : t('print.print')}</Button>
            <Button variant="blue" icon="design" block disabled={!!busy} onClick={edit} data-testid="edit-in-design">{busy === 'edit' ? t('common.opening') : d.designJsonUrl ? t('library.detail.edit') : t('library.detail.openIn')}</Button>
            <Button icon="down" block disabled={!!busy} onClick={download}>{t('common.download')}</Button>
            <Button icon="trash" block disabled={!!busy} onClick={() => setConfirm(true)}>{t('common.delete')}</Button>
          </div>
          {err && <p className="bv-error-text" role="alert">{err}</p>}
        </div>
      </div>
      <Modal open={confirm} title={t('library.detail.deleteTitle')} onClose={() => setConfirm(false)}>
        <p className="bv-muted">{t('library.detail.deleteBody', { name: d.name })}</p>
        <div className="bv-modal-actions">
          <Button onClick={() => setConfirm(false)}>{t('common.cancel')}</Button>
          <Button variant="coral" disabled={busy === 'delete'} onClick={remove}>{t('library.detail.deleteConfirm')}</Button>
        </div>
      </Modal>
    </Modal>
  );
}

function NewFolder({ open, onClose, onCreate }) {
  const { t } = useTranslation('bv');
  const [name, setName] = useState('');
  const [color, setColor] = useState('purple');
  useEffect(() => { if (open) { setName(''); setColor('purple'); } }, [open]);
  return (
    <Modal open={open} title={t('library.newFolder.title')} onClose={onClose}>
      <form className="bv-newfolder" onSubmit={(e) => { e.preventDefault(); if (name.trim()) onCreate(name.trim(), color); }}>
        <label className="bv-label" htmlFor="bv-fname">{t('common.name')}</label>
        <Input id="bv-fname" value={name} maxLength={120} autoFocus onChange={(e) => setName(e.target.value)} placeholder={t('library.newFolder.placeholder')} />
        <span className="bv-label">{t('library.newFolder.colour')}</span>
        <div className="bv-newfolder__colors" role="radiogroup" aria-label={t('library.newFolder.colour')}>
          {KIT.map((c) => (
            <button key={c} type="button" role="radio" aria-checked={color === c} aria-label={c} className={`bv-newfolder__swatch bv-dot--${c}${color === c ? ' is-on' : ''}`} onClick={() => setColor(c)} />
          ))}
          <label className={`bv-newfolder__swatch bv-newfolder__custom${isHex(color) ? ' is-on' : ''}`} style={isHex(color) ? { background: color } : undefined} title={t('library.newFolder.anyColour')}>
            <input type="color" aria-label={t('library.newFolder.anyColour')} value={isHex(color) ? color : '#7f4bf6'} onChange={(e) => setColor(e.target.value)} data-testid="folder-color" />
          </label>
        </div>
        <div className="bv-modal-actions">
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" variant="green" icon="plus" disabled={!name.trim()}>{t('library.newFolder.create')}</Button>
        </div>
      </form>
    </Modal>
  );
}

export default function LibraryPage() {
  const { t } = useTranslation('bv');
  const [params, setParams] = useSearchParams();
  // ?tab=canva (back from Canva sign-in) opens the Canva designs tab.
  const [tab, setTab] = useState(() => (new URLSearchParams(window.location.search).get('tab') === 'canva' ? 'canva' : 'yours'));
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('newest');
  const [folder, setFolder] = useState(''); // '' all · '__none' default · cat_uuid
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [cats, setCats] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selecting, setSelecting] = useState(false);
  const [sel, setSel] = useState(() => new Set());
  const [open, setOpen] = useState(null);
  const [newFolder, setNewFolder] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [dropOn, setDropOn] = useState(null);
  const fileInput = useRef(null);
  const navigate = useNavigate();
  const store = usePrintStore();
  const notify = (text, tone = 'green', ms) => store && store.notify(text, tone, ms);

  useEffect(() => { const t = setTimeout(() => setQ(search.trim()), 300); return () => clearTimeout(t); }, [search]);
  useEffect(() => { setPage(1); }, [q, sort, folder, tab]);

  const loadMeta = useCallback(async () => {
    const [c, s] = await Promise.all([getCategories(), librarySummary()]);
    if (c.ok) setCats(c.categories || []);
    if (s.ok) setSummary(s);
  }, []);
  const load = useCallback(async () => {
    setErr(null);
    const r = await listDesigns({ page, limit: PAGE, search: q, sort, category: folder, source: tab === 'canva' ? 'canva' : '' });
    if (r.ok) setData(r); else { setErr(r.error); setData({ designs: [], total: 0 }); }
  }, [page, q, sort, folder, tab]);
  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { setData(null); load(); }, [load]);
  // A poster made in the background (AI Poster Maker) lands in the library: refresh (desktop "reload on background saves").
  useEffect(() => {
    const on = () => { load(); loadMeta(); };
    window.addEventListener('library-designs-changed', on);
    return () => window.removeEventListener('library-designs-changed', on);
  }, [load, loadMeta]);
  // ?open=<id> (Home "Recent designs")
  useEffect(() => {
    const id = params.get('open');
    if (id && data && data.designs) {
      const d = data.designs.find((x) => x.id === id);
      if (d) setOpen(d);
      params.delete('open'); setParams(params, { replace: true });
    }
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const folders = useMemo(() => cats.map((c, i) => ({ ...c, color: folderColor(c, i) })), [cats]);
  const folderOf = (d) => folders.find((f) => f.cat_uuid === d.category) || null;
  const pages = data ? Math.max(1, Math.ceil((data.total || 0) / PAGE)) : 1;
  const replace = (d) => setData((x) => ({ ...x, designs: x.designs.map((y) => (y.id === d.id ? { ...y, ...d } : y)) }));

  const favorite = async (d, e) => {
    e.stopPropagation();
    const on = !(d.metadata && d.metadata.favorite);
    replace({ ...d, metadata: { ...(d.metadata || {}), favorite: on } });
    const r = await updateDesign(d.id, { favorite: on });
    if (!r.ok) { replace(d); notify(r.error || t('library.msg.favFailed'), 'coral'); return; }
    feedbackFavorite(d, on);
  };
  const createFolder = async (name, color) => {
    const next = [...cats, { cat_uuid: crypto.randomUUID(), cat_name: name, cat_color: isHex(color) ? color : KIT_HEX[color] }];
    const r = await putCategories(next);
    if (r.ok) { setCats(r.categories); notify(t('library.msg.folderCreated', { name })); } else setErr(r.error);
    setNewFolder(false);
  };
  const folderName = (cat) => (cat ? (folders.find((f) => f.cat_uuid === cat) || {}).cat_name || t('library.folder').toLowerCase() : t('library.defaultFolder'));
  const moveIds = async (ids, cat) => {
    let bad = 0;
    for (const id of ids) { const r = await updateDesign(id, { category: cat || null }); if (!r.ok) bad++; }
    const ok = ids.length - bad;
    if (ok) notify(t('library.msg.moved', { count: ok, folder: folderName(cat) }));
    if (bad) notify(t('library.msg.notMoved', { count: bad }), 'coral');
    load(); loadMeta();
  };
  const moveSelected = async (cat) => { const ids = [...sel]; setSel(new Set()); setSelecting(false); await moveIds(ids, cat); };
  const deleteSelected = async () => {
    const ids = [...sel];
    setConfirmBulk(false);
    let bad = 0;
    for (const id of ids) { const r = await deleteDesign(id); if (!r.ok) bad++; }
    const ok = ids.length - bad;
    if (ok) notify(t('library.msg.deleted', { count: ok }));
    if (bad) notify(t('library.msg.notDeleted', { count: bad }), 'coral');
    setSel(new Set()); setSelecting(false); load(); loadMeta();
  };
  /** Print selected (desktop "Add to print queue"): every selected design goes to the Print page. */
  const printSelected = () => {
    const ds = ((data && data.designs) || []).filter((d) => sel.has(d.id) && (d.imageUrl || d.thumbnailUrl));
    if (!ds.length) return;
    handToPrint(ds.map(printItem));
    navigate('/print');
  };
  /** Drag a card onto a folder (dragging a selected card moves the whole selection). */
  const dropProps = (id) => ({
    onDragOver: (e) => { if ([...e.dataTransfer.types].includes('application/x-bv-design')) { e.preventDefault(); setDropOn(id); } },
    onDragLeave: () => setDropOn((x) => (x === id ? null : x)),
    onDrop: (e) => {
      e.preventDefault(); setDropOn(null);
      const dragged = e.dataTransfer.getData('application/x-bv-design');
      if (!dragged) return;
      const ids = sel.has(dragged) ? [...sel] : [dragged];
      if (sel.has(dragged)) { setSel(new Set()); setSelecting(false); }
      moveIds(ids, id === '__none' ? '' : id);
    },
  });
  /** Upload (desktop PrintLibrary): pictures and PDFs up to 200 MB each. A PDF is saved one design per page
   * (shared rows stay pictures every StudioCore app can show), each tagged "Page n of N". One summary at the end. */
  const upload = async (list) => {
    const items = [];
    const failures = [];
    for (const f of list) {
      if (f.size > UPLOAD_MAX_BYTES) { failures.push(`${f.name}: ${t('library.upload.tooBig')}`); continue; }
      if (f.type === 'application/pdf' || /\.pdf$/i.test(f.name)) {
        setUploading(t('print.reading', { name: f.name }));
        try {
          const { pdfToImages } = await import('../features/print/pdfImport.js');
          const pagesOut = (await pdfToImages(f)).files;
          const base = f.name.replace(/\.pdf$/i, '');
          pagesOut.forEach((pf, i) => items.push({ file: pf, name: pagesOut.length > 1 ? `${base} — ${t('library.upload.pageN', { n: i + 1 })}` : base, meta: pagesOut.length > 1 ? { pageCount: pagesOut.length, pageIndex: i, pdfName: f.name } : null }));
        } catch (e) { failures.push(`${f.name}: ${e.message || t('library.upload.pdfFailed')}`); }
      } else if (/^image\//.test(f.type)) items.push({ file: f, name: f.name.replace(/\.[a-z0-9]+$/i, ''), meta: null });
      else failures.push(`${f.name}: ${t('library.upload.notPicture')}`);
    }
    let n = 0;
    let saved = 0;
    for (const it of items) {
      setUploading(t('library.upload.saving', { n: ++n, total: items.length }));
      try {
        const t = await makeThumbnail(it.file);
        const r = await saveDesign({ name: it.name, source: 'upload', width: t.width, height: t.height, ...(folder && folder !== '__none' ? { category: folder } : {}), ...(it.meta ? { metadata: JSON.stringify(it.meta) } : {}) }, { thumbnail: t.file, file: it.file, ...t.previews });
        if (r.ok) saved++; else failures.push(`${it.name}: ${r.error}`);
      } catch (e) { failures.push(`${it.name}: ${t('library.upload.badType')}`); }
    }
    setUploading(null);
    const added = saved ? t('library.upload.added', { count: saved }) : '';
    if (!failures.length) { if (added) notify(added); } else {
      notify(`${added ? `${added} · ` : ''}${t('library.upload.notAdded', { count: failures.length, list: failures.slice(0, 3).join('; ') + (failures.length > 3 ? '…' : '') })}`, saved ? 'yellow' : 'coral', 10000);
    }
    load(); loadMeta();
  };

  const count = (id) => (summary ? (id === '' ? summary.total : id === '__none' ? summary.uncategorised : summary.byCategory[id] || 0) : null);
  const folderRow = (id, label, color, icon) => (
    <button key={id || 'all'} type="button" className={`bv-folder${folder === id ? ' is-active' : ''}${dropOn === id ? ' is-drop' : ''}`} aria-pressed={folder === id} onClick={() => setFolder(id)}
      data-folder={id || 'all'} {...(id ? dropProps(id) : {})}>
      {icon ? <Icon name={icon} size={20} /> : <span {...dotProps(color)} />}
      <span className="bv-folder__name">{label}</span>
      {count(id) != null && <span className="bv-folder__count">{count(id)}</span>}
    </button>
  );

  return (
    <div className="bv-content bv-library">
      <TopBar title={t('library.title')} subtitle={t('library.subtitle')} actions={(
        <>
          <Button icon="check" onClick={() => { setSelecting((s) => !s); setSel(new Set()); }} aria-pressed={selecting} data-testid="select-button">{selecting ? t('common.done') : t('common.select')}</Button>
          <Button variant="blue" icon="upload" onClick={() => fileInput.current && fileInput.current.click()} disabled={!!uploading} data-testid="upload-button">{uploading ? t('print.card.uploading') : t('print.upload')}</Button>
          <input ref={fileInput} type="file" hidden multiple accept="image/*,application/pdf,.pdf" onChange={(e) => { upload([...e.target.files]); e.target.value = ''; }} data-testid="upload-input" />
        </>
      )} />
      <div className="bv-libgrid">
        <Card className="bv-libside">
          <SearchInput placeholder={t('library.search')} value={search} onChange={(e) => setSearch(e.target.value)} aria-label={t('library.search')} />
          <div className="bv-label">{t('common.sortBy')}</div>
          <Select value={sort} onChange={(e) => setSort(e.target.value)} options={SORTS.map((s) => ({ ...s, label: t(s.label) }))} aria-label={t('common.sortBy')} />
          <div className="bv-libside__head">
            <span className="bv-label">{t('library.folders')}</span>
            <button type="button" className="bv-linkbtn" onClick={() => setNewFolder(true)} data-testid="new-folder">{t('library.newPlus')}</button>
          </div>
          <div className="bv-folders" data-testid="folders">
            {folderRow('', t('library.allDesigns'), null, 'library')}
            {folders.map((f) => folderRow(f.cat_uuid, f.cat_name, f.color))}
            {folderRow('__none', t('library.defaultFolder'), 'grey')}
          </div>
        </Card>

        <div className="bv-libmain">
          <div className="bv-libmain__head">
            <span data-tour="library-canva-tab"><Segmented value={tab} onChange={setTab} options={[{ value: 'yours', label: t('library.tabs.yours') }, { value: 'canva', label: t('library.tabs.canva') }]} /></span>
            <span className="bv-libmain__count" data-testid="library-count">{tab === 'canva' ? '' : data ? `${t('library.designs', { count: data.total })} · ${t('library.pageOfLower', { n: page, total: pages })}` : ''}</span>
          </div>
          {uploading && <p className="bv-small bv-muted" role="status">{uploading}</p>}
          {err && <p className="bv-error-text" role="alert">{err}</p>}
          {tab === 'canva' ? <LibraryCanva /> : !data ? (
            <div className="bv-dgrid">{Array.from({ length: PAGE }, (_, i) => <Skeleton key={i} height={300} radius={24} />)}</div>
          ) : data.designs.length === 0 ? (
            <Card><EmptyState icon="library" title={q ? t('common.noMatch') : t('library.empty.title')}>
              {q ? t('common.tryAnotherWord') : t('library.empty.body')}
            </EmptyState></Card>
          ) : (
            <div className="bv-dgrid" data-testid="design-grid">
              {data.designs.map((d) => (
                <DesignCard key={d.id} d={d} folder={folderOf(d)} selecting={selecting} selected={sel.has(d.id)}
                  onOpen={() => setOpen(d)} onFavorite={(e) => favorite(d, e)}
                  onToggle={() => setSel((s) => { const n = new Set(s); n.has(d.id) ? n.delete(d.id) : n.add(d.id); return n; })} />
              ))}
            </div>
          )}
          {selecting && sel.size > 0 && (
            <div className="bv-selbar" role="toolbar" aria-label={t('library.selectedAria')} data-testid="selection-bar">
              <b>{t('common.nSelected', { count: sel.size })}</b>
              <Select aria-label={t('library.moveTo')} value="" onChange={(e) => e.target.value && moveSelected(e.target.value === '__none' ? '' : e.target.value)}
                options={[{ value: '', label: t('library.moveToDots') }, { value: '__none', label: t('library.defaultFolder') }, ...folders.map((f) => ({ value: f.cat_uuid, label: f.cat_name }))]} />
              <Button variant="coral" icon="print" onClick={printSelected} data-testid="print-selected">{t('print.print')}</Button>
              <Button icon="trash" onClick={() => setConfirmBulk(true)} data-testid="delete-selected">{t('common.delete')}</Button>
            </div>
          )}
          {data && pages > 1 && (
            <div className="bv-pager">
              <Button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>{t('common.previous')}</Button>
              <span className="bv-pager__label">{t('library.pageOf', { n: page, total: pages })}</span>
              <Button variant="blue" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>{t('common.next')}</Button>
            </div>
          )}
        </div>
      </div>
      {open && <DesignDetail d={open} folders={folders} onClose={() => setOpen(null)}
        onChanged={(d) => { replace(d); setOpen(d); loadMeta(); }} onDeleted={(id) => { setOpen(null); setData((x) => ({ ...x, designs: x.designs.filter((y) => y.id !== id), total: x.total - 1 })); loadMeta(); }} />}
      <NewFolder open={newFolder} onClose={() => setNewFolder(false)} onCreate={createFolder} />
      <Modal open={confirmBulk} title={t('library.bulk.title', { count: sel.size })} onClose={() => setConfirmBulk(false)}>
        <p className="bv-muted">{t('library.bulk.body')}</p>
        <div className="bv-modal-actions">
          <Button onClick={() => setConfirmBulk(false)}>{t('common.cancel')}</Button>
          <Button variant="coral" onClick={deleteSelected} data-testid="confirm-bulk-delete">{t('library.bulk.confirm', { count: sel.size })}</Button>
        </div>
      </Modal>
    </div>
  );
}
